import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

export const SUPPORTED_LOCALES = [
  { value: 'en-CA', label: 'English (Canada)' },
  { value: 'en-US', label: 'English (United States)' },
  { value: 'de-DE', label: 'Deutsch (Deutschland)' },
];

const messages = {
  en: {
    dashboard: 'Dashboard', clients: 'Clients', projects: 'Projects', invoices: 'Invoices',
    creditNotes: 'Credit Notes', expenses: 'Expenses', banking: 'Banking', templates: 'Templates',
    reports: 'Reports', accountantHandover: 'Accountant Handover', settings: 'Settings', help: 'Help',
    signOut: 'Sign Out', admin: 'Admin', loading: 'Loading...',
  },
  de: {
    dashboard: 'Übersicht', clients: 'Kunden', projects: 'Projekte', invoices: 'Rechnungen',
    creditNotes: 'Gutschriften', expenses: 'Ausgaben', banking: 'Bankabgleich', templates: 'Vorlagen',
    reports: 'Berichte', accountantHandover: 'Steuerberater-Übergabe', settings: 'Einstellungen', help: 'Hilfe',
    signOut: 'Abmelden', admin: 'Administration', loading: 'Wird geladen...',
  },
};

const LocaleContext = createContext(null);

export function LocaleProvider({ children }) {
  const { user } = useAuth();
  const [locale, setLocaleState] = useState(() => localStorage.getItem('sf_locale') || 'en-CA');

  const setLocale = useCallback((nextLocale) => {
    const supported = SUPPORTED_LOCALES.some((item) => item.value === nextLocale) ? nextLocale : 'en-CA';
    localStorage.setItem('sf_locale', supported);
    document.documentElement.lang = supported.startsWith('de') ? 'de' : 'en';
    setLocaleState(supported);
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale.startsWith('de') ? 'de' : 'en';
  }, [locale]);

  useEffect(() => {
    if (!user) return;
    api.get('/account')
      .then((response) => setLocale(response.data.default_locale || 'en-CA'))
      .catch(() => {});
  }, [user, setLocale]);

  const language = locale.startsWith('de') ? 'de' : 'en';
  const t = useCallback((key) => messages[language][key] || messages.en[key] || key, [language]);
  const value = useMemo(() => ({ locale, language, setLocale, t }), [locale, language, setLocale, t]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  const context = useContext(LocaleContext);
  if (!context) throw new Error('useLocale must be used within LocaleProvider');
  return context;
}
